/**
 * PMD Ads Header Bidding Controller
 *
 * Manages Prebid.js and Amazon APS (TAM) header bidding integration.
 * This script coordinates bid requests before GPT ad calls.
 */

( function( window, document ) {
	'use strict';

	// Initialize namespace.
	window.pmdAds = window.pmdAds || {};

	/**
	 * Header Bidding Controller.
	 */
	window.pmdAds.headerBidding = {
		initialized: false,
		config: null,
		bidTimeout: 1500,
		amazonReady: false,
		prebidReady: false,
		bidsRequested: false,
		bidsReceived: false,
		adUnits: [],
		callbacks: [],

		/**
		 * Initialize header bidding.
		 */
		init: function() {
			if ( this.initialized ) {
				return;
			}

			this.config = ( window.pmdAdsConfig && window.pmdAdsConfig.headerBidding ) || {};
			this.initialized = true;

			if ( ! this.config.enabled ) {
				this.log( 'Header bidding disabled - no bidders configured' );
				this.signalReady();
				return;
			}

			this.bidTimeout = this.config.bidTimeout || 2000;
			this.log( 'Initializing header bidding with timeout: ' + this.bidTimeout + 'ms' );

			// Initialize bidders.
			this.initAmazon();
			this.initPrebid();
		},

		/**
		 * Check if debug mode is active via body class.
		 *
		 * The pmd-ads-debug class is added server-side by ScriptLoader
		 * when ?pmd_ads_debug=1 is in the URL.
		 *
		 * @return {boolean} True if debug mode is active.
		 */
		isDebugMode: function() {
			return document.body && document.body.classList.contains( 'pmd-ads-debug' );
		},

		/**
		 * Log messages in debug mode only.
		 * Logs appear when ?pmd_ads_debug=1 is in the URL.
		 *
		 * @param {string} message Message to log.
		 * @param {*} data Optional data to log.
		 */
log: function( message, data ) {
		if ( ! this.isDebugMode() ) {
			return;
		}

		// For large objects (bid responses, etc), log only a summary to prevent bloat.
		if ( data !== undefined && typeof data === 'object' ) {
			try {
				var jsonStr = JSON.stringify( data );
				var sizeKB = jsonStr.length / 1024;

				// If data is > 100KB, log only size/summary instead of full object.
				if ( sizeKB > 100 ) {
					console.log( '[PMD Header Bidding] ' + message + ' (size: ' + sizeKB.toFixed( 2 ) + 'KB - not logged to prevent memory bloat)' );
					return;
				}
			} catch ( e ) {
				// Continue with normal logging if stringify fails.
			}

			console.log( '[PMD Header Bidding] ' + message, data );
		} else {
			console.log( '[PMD Header Bidding] ' + message );
		}
	},

		/**
		 * Initialize Amazon APS (TAM).
		 */
		initAmazon: function() {
			var amazonConfig = this.config.bidders && this.config.bidders.amazon;

			if ( ! amazonConfig || ! amazonConfig.enabled ) {
				this.log( 'Amazon APS not configured' );
				this.amazonReady = true;
				return;
			}

			var self = this;

			// Wait for apstag to be available.
			this.waitFor( function() {
				return typeof window.apstag !== 'undefined';
			}, function() {
				self.log( 'Initializing Amazon APS with Publisher ID: ' + amazonConfig.publisherId );

				window.apstag.init( {
					pubID: amazonConfig.publisherId,
					adServer: 'googletag',
					bidTimeout: self.bidTimeout
				} );

				self.amazonReady = true;
				self.log( 'Amazon APS initialized' );
			}, 5000 );
		},

		/**
		 * Initialize Prebid.js.
		 */
		initPrebid: function() {
			var self = this;
			var bidders = this.config.bidders || {};

			// Check if any Prebid bidders are configured (non-Amazon).
			var hasPrebidBidders = Object.keys( bidders ).some( function( key ) {
				return key !== 'amazon' && bidders[ key ] && bidders[ key ].enabled;
			} );

			if ( ! hasPrebidBidders ) {
				this.log( 'No Prebid bidders configured' );
				this.prebidReady = true;
				return;
			}

			// Prebid setConfig is owned by the Demand Manager wrapper; overriding here suppresses bidder-specific GAM key-values (hb_pb_rubicon, etc.).
			this.waitFor( function() {
				return typeof window.pbjs !== 'undefined';
			}, function() {
				window.pbjs = window.pbjs || {};
				window.pbjs.que = window.pbjs.que || [];

				self.prebidReady = true;
				self.log( 'Prebid.js initialized' );
			}, 5000 );
		},

		/**
		 * Wait for a condition to be true.
		 *
		 * @param {Function} condition Function that returns true when ready.
		 * @param {Function} callback Callback to execute when ready.
		 * @param {number} timeout Maximum time to wait in ms.
		 */
		waitFor: function( condition, callback, timeout ) {
			var self = this;
			var startTime = Date.now();
			var interval = 50;

			var check = function() {
				if ( condition() ) {
					callback();
				} else if ( Date.now() - startTime < timeout ) {
					setTimeout( check, interval );
				} else {
					if ( self.isDebugMode() ) {
						console.warn( '[PMD Header Bidding] Timeout waiting for condition' );
					}
					callback(); // Call anyway to prevent blocking.
				}
			};

			check();
		},

		/**
		 * Build ad units for header bidding from GPT slot configuration.
		 *
		 * @param {Array} slots Array of slot configurations from pmdAdsConfig.
		 * @return {Array} Ad units for Prebid/Amazon.
		 */
		buildAdUnits: function( slots ) {
			var self = this;
			var bidders = this.config.bidders || {};
			var adUnits = [];

			if ( ! slots || ! slots.length ) {
				return adUnits;
			}

			slots.forEach( function( slotConfig ) {
				var adUnit = {
					code: slotConfig.divId,
					mediaTypes: {
						banner: {
							sizes: slotConfig.sizes || []
						}
					},
					bids: []
				};

				// Add Magnite/Rubicon bidder.
				if ( bidders.magnite && bidders.magnite.enabled ) {
					adUnit.bids.push( {
						bidder: 'rubicon',
						params: {
							accountId: parseInt( bidders.magnite.accountId, 10 ),
							siteId: parseInt( bidders.magnite.siteId || bidders.magnite.accountId, 10 ),
							zoneId: parseInt( bidders.magnite.zoneId || bidders.magnite.accountId, 10 )
						}
					} );
				}

				// Add Index Exchange bidder.
				if ( bidders.indexExchange && bidders.indexExchange.enabled ) {
					adUnit.bids.push( {
						bidder: 'ix',
						params: {
							siteId: bidders.indexExchange.siteId
						}
					} );
				}

				// Add OpenX bidder.
				if ( bidders.openx && bidders.openx.enabled ) {
					adUnit.bids.push( {
						bidder: 'openx',
						params: {
							delDomain: bidders.openx.delDomain,
							unit: slotConfig.slotId || slotConfig.divId
						}
					} );
				}

				// Add AppNexus/Xandr bidder.
				if ( bidders.appnexus && bidders.appnexus.enabled ) {
					adUnit.bids.push( {
						bidder: 'appnexus',
						params: {
							placementId: parseInt( bidders.appnexus.placementId, 10 )
						}
					} );
				}

				// Add PubMatic bidder.
				if ( bidders.pubmatic && bidders.pubmatic.enabled ) {
					adUnit.bids.push( {
						bidder: 'pubmatic',
						params: {
							publisherId: bidders.pubmatic.publisherId,
							adSlot: slotConfig.slotId || slotConfig.divId
						}
					} );
				}

				// Only add ad unit if it has bids configured.
				if ( adUnit.bids.length > 0 ) {
					adUnits.push( adUnit );
				}
			} );

			this.adUnits = adUnits;
			return adUnits;
		},

		/**
		 * Build Amazon APS slots from GPT slot configuration.
		 *
		 * @param {Array} slots Array of slot configurations.
		 * @return {Array} Amazon APS slot definitions.
		 */
		buildAmazonSlots: function( slots ) {
			if ( ! slots || ! slots.length ) {
				return [];
			}

			return slots.map( function( slotConfig ) {
				return {
					slotID: slotConfig.divId,
					slotName: slotConfig.path || slotConfig.divId,
					sizes: slotConfig.sizes || []
				};
			} );
		},

		/**
		 * Request bids from all configured bidders.
		 *
		 * @param {Array} slots GPT slot configurations.
		 * @param {Function} callback Callback when bids are ready.
		 */
		requestBids: function( slots, callback ) {
			var self = this;
			var callbackCalled = false;

			// Ensure config is loaded.
			if ( ! this.config ) {
				this.config = ( window.pmdAdsConfig && window.pmdAdsConfig.headerBidding ) || {};
			}

			// Safety wrapper to ensure callback is only called once.
			var safeCallback = function() {
				if ( callbackCalled ) {
					return;
				}
				callbackCalled = true;
				self.bidsReceived = true;
				callback();
			};

			if ( ! this.config || ! this.config.enabled ) {
				self.log( 'Header bidding disabled, skipping bid request' );
				safeCallback();
				return;
			}

			if ( this.bidsRequested ) {
				self.log( 'Bids already requested' );
				safeCallback();
				return;
			}

			this.bidsRequested = true;
			this.log( 'Requesting bids for ' + slots.length + ' slots' );

			var amazonDone = false;
			var prebidDone = false;

			// Timeout fallback.
			var timeoutId = setTimeout( function() {
				self.log( 'Bid timeout reached (' + self.bidTimeout + 'ms)' );
				safeCallback();
			}, this.bidTimeout );

			var checkComplete = function() {
				if ( callbackCalled ) {
					return;
				}

				if ( amazonDone && prebidDone ) {
					clearTimeout( timeoutId );
					self.log( 'All bids received' );
					safeCallback();
				}
			};

			// Request Amazon bids.
			this.requestAmazonBids( slots, function() {
				amazonDone = true;
				self.log( 'Amazon bids complete' );
				checkComplete();
			} );

			// Request Prebid bids.
			this.requestPrebidBids( slots, function() {
				prebidDone = true;
				self.log( 'Prebid bids complete' );
				checkComplete();
			} );
		},

		/**
		 * Request bids from Amazon APS.
		 *
		 * @param {Array} slots GPT slot configurations.
		 * @param {Function} callback Callback when done.
		 */
		requestAmazonBids: function( slots, callback ) {
			var self = this;
			var amazonConfig = this.config.bidders && this.config.bidders.amazon;

			if ( ! amazonConfig || ! amazonConfig.enabled || ! this.amazonReady ) {
				callback();
				return;
			}

			// Wait for apstag to be fully ready.
			this.waitFor( function() {
				return typeof window.apstag !== 'undefined' && typeof window.apstag.fetchBids === 'function';
			}, function() {
				var amazonSlots = self.buildAmazonSlots( slots );
				self.log( 'Requesting Amazon bids for slots:', amazonSlots );

				window.apstag.fetchBids( {
					slots: amazonSlots,
					timeout: self.bidTimeout
				}, function( bids ) {
					self.log( 'Amazon bids received:', bids );
					callback();
				} );
			}, 3000 );
		},

		/**
		 * Request bids from Prebid.js.
		 *
		 * @param {Array} slots GPT slot configurations.
		 * @param {Function} callback Callback when done.
		 */
		requestPrebidBids: function( slots, callback ) {
			var self = this;

			if ( ! this.prebidReady ) {
				callback();
				return;
			}

			// Demand Manager mode: use pbjs.rp.requestBids() — bidder config is managed via DM dashboard.
			if ( this.config.useDemandManager ) {
				this.waitFor( function() {
					return typeof window.pbjs !== 'undefined' && window.pbjs.rp && typeof window.pbjs.rp.requestBids === 'function'
						&& window.googletag && window.googletag.cmd && typeof window.googletag.cmd.push === 'function';
				}, function() {
					// Run after slot registration commands, including when GPT is still loading.
					window.googletag.cmd.push( function() {
						// Resolve live GPT objects so refreshes only auction the requested slots.
						const registeredSlots = window.googletag.pubads().getSlots();
						const gptSlotObjects = registeredSlots.filter( function( gptSlot ) {
							return slots.some( function( slot ) {
								return slot.divId === gptSlot.getSlotElementId();
							} );
						} );

						// Never fall back to a page-wide auction when no requested slots exist.
						if ( ! gptSlotObjects.length ) {
							callback();
							return;
						}

						self.log( 'Requesting Demand Manager bids for slots: ' + gptSlotObjects.map( function( slot ) {
							return slot.getSlotElementId();
						} ).join( ', ' ) );

						window.pbjs.rp.requestBids( {
							gptSlotObjects: gptSlotObjects,
							callback: function( bidResponses ) {
								self.log( 'Demand Manager bids received:', bidResponses );
								callback();
							}
						} );
					} );
				}, 3000 );
				return;
			}

			// Standard Prebid mode: build ad units and use pbjs.requestBids().
			var adUnits = this.buildAdUnits( slots );

			if ( ! adUnits.length ) {
				callback();
				return;
			}

			// Wait for pbjs to be fully ready.
			this.waitFor( function() {
				return typeof window.pbjs !== 'undefined' && window.pbjs.que;
			}, function() {
				window.pbjs.que.push( function() {
					self.log( 'Requesting Prebid bids for ad units:', adUnits );

					window.pbjs.addAdUnits( adUnits );
					window.pbjs.requestBids( {
						bidsBackHandler: function( bidResponses ) {
							self.log( 'Prebid bids received:', bidResponses );
							callback();
						},
						timeout: self.bidTimeout
					} );
				} );
			}, 3000 );
		},

		/**
		 * Set bid targeting on GPT slots.
		 * Called after bids are received and before GPT displays ads.
		 */
		setTargetingForGPT: function() {
			var self = this;

			if ( ! this.bidsReceived ) {
				this.log( 'No bids received, skipping targeting' );
				return;
			}

			// Set Amazon targeting.
			if ( this.config.bidders && this.config.bidders.amazon && this.config.bidders.amazon.enabled ) {
				this.waitFor( function() {
					return typeof window.apstag !== 'undefined' && typeof window.apstag.setDisplayBids === 'function';
				}, function() {
					window.apstag.setDisplayBids();
					self.log( 'Amazon targeting set on GPT' );
				}, 1000 );
			}

			// Set Prebid targeting.
			if ( this.adUnits.length > 0 ) {
				this.waitFor( function() {
					return typeof window.pbjs !== 'undefined' && window.pbjs.setTargetingForGPTAsync;
				}, function() {
					window.pbjs.que.push( function() {
						window.pbjs.setTargetingForGPTAsync();
						self.log( 'Prebid targeting set on GPT' );
					} );
				}, 1000 );
			}
		},

		/**
		 * Signal that header bidding is ready.
		 * This allows pmd-ads.js to proceed with GPT initialization.
		 */
		signalReady: function() {
			this.log( 'Header bidding ready' );

			// Execute any registered callbacks.
			this.callbacks.forEach( function( cb ) {
				try {
					cb();
				} catch ( e ) {
					console.error( '[PMD Header Bidding] Callback error:', e );
				}
			} );

			// Dispatch custom event for external integrations.
			var event = new CustomEvent( 'pmdHeaderBiddingReady', {
				detail: {
					bidsReceived: this.bidsReceived,
					adUnits: this.adUnits
				}
			} );
			document.dispatchEvent( event );
		},

		/**
		 * Register a callback to be called when header bidding is ready.
		 *
		 * @param {Function} callback Callback function.
		 */
		onReady: function( callback ) {
			if ( typeof callback !== 'function' ) {
				return;
			}

			if ( this.bidsReceived || ( this.initialized && ! this.config.enabled ) ) {
				callback();
			} else {
				this.callbacks.push( callback );
			}
		},

		/**
		 * Request bids and set targeting, then execute callback.
		 * This is the main entry point called from pmd-ads.js.
		 *
		 * @param {Array} slots GPT slot configurations.
		 * @param {Function} callback Callback when ready to display ads.
		 */
		runAuction: function( slots, callback ) {
			var self = this;
			var callbackCalled = false;

			// Ensure config is loaded (in case init hasn't been called yet).
			if ( ! this.config ) {
				this.config = ( window.pmdAdsConfig && window.pmdAdsConfig.headerBidding ) || {};
			}

			// Safety wrapper to ensure callback is only called once.
			var safeCallback = function() {
				if ( callbackCalled ) {
					return;
				}
				callbackCalled = true;
				callback();
			};

			if ( ! this.config || ! this.config.enabled ) {
				safeCallback();
				return;
			}

			// Absolute fallback timeout - never block ads for more than 3 seconds.
			var fallbackTimeout = setTimeout( function() {
				if ( ! callbackCalled ) {
					self.log( 'Fallback timeout - proceeding without bids' );
					self.bidsReceived = true;
					safeCallback();
				}
			}, 3000 );

			// Wait for bidders to be initialized.
			this.waitFor( function() {
				return self.amazonReady && self.prebidReady;
			}, function() {
				self.requestBids( slots, function() {
					clearTimeout( fallbackTimeout );
					self.setTargetingForGPT();
					self.signalReady();
					safeCallback();
				} );
			}, this.bidTimeout );
		},

		/**
		 * Refresh bids for specific slots (used during ad refresh).
		 *
		 * @param {Array} slots Slot configurations to refresh.
		 * @param {Function} callback Callback when bids are ready.
		 */
		refreshBids: function( slots, callback ) {
			var self = this;

			// Ensure config is loaded.
			if ( ! this.config ) {
				this.config = ( window.pmdAdsConfig && window.pmdAdsConfig.headerBidding ) || {};
			}

			if ( ! this.config || ! this.config.enabled ) {
				callback();
				return;
			}

			this.bidsRequested = false; // Allow new bid request.
			this.bidsReceived = false;

			this.log( 'Refreshing bids for ' + slots.length + ' slots' );

			this.requestBids( slots, function() {
				self.setTargetingForGPT();
				callback();
			} );
		}
	};

	// Initialize when DOM is ready.
	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', function() {
			window.pmdAds.headerBidding.init();
		} );
	} else {
		window.pmdAds.headerBidding.init();
	}

} )( window, document );
